import { z } from "zod";
import { zString } from "./zod_utils.js";
/**
 * Admin-editable retailer settings (D5, D8). One `retailer_settings`
 * row per key; env values are only the seeds used while a key is unset. The
 * brand (name, logo, icon, colours) is env-only and not a setting.
 */
export const WATERMARK_POSITION_ENUM = ["top-left", "top-right", "bottom-left", "bottom-right"];
export const watermarkPositionZod = z.enum(WATERMARK_POSITION_ENUM);
/** Most garment types one retailer can offer on the counter. */
export const MAX_GARMENT_NOUNS = 8;
/** One garment type as the counter offers it (English; sent to Refract as `garmentNoun`). */
export const garmentNounZod = zString.min(1).max(40);
/** `#rrggbb` (lower- or upper-case hex). */
export const zHexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a #rrggbb colour");
/**
 * The `branding` settings key. The brand itself (app name, logo, app icon,
 * accent and dark colours) comes only from the backend env (ADR-024); this key
 * holds just the product types the counter offers. Rows saved when the brand
 * was editable still carry those fields: they are stripped on read.
 */
export const brandingSettingZod = z.object({
    // Garment types the counter offers, in English (sent to Refract as
    // `garmentNoun`). The first is the default; with one entry the counter
    // shows no picker.
    garmentNouns: z
        .array(garmentNounZod)
        .min(1)
        .max(MAX_GARMENT_NOUNS)
        .refine((nouns) => new Set(nouns.map((noun) => noun.toLowerCase())).size === nouns.length, { message: "Each garment type must be different" }),
});
/** Public branding (from env) served to every page (`branding.get`, `/api/brand/branding.json`). */
export const publicBrandingZod = z.object({
    appName: zString,
    logoUrl: z.url().nullable(),
    iconUrl: z.url().nullable(),
});
/**
 * The configured spelling of `noun` (case-insensitive match), or null when the
 * retailer does not offer it (any more).
 */
export const matchGarmentNoun = (nouns, noun) => {
    if (!noun)
        return null;
    const wanted = noun.trim().toLowerCase();
    return nouns.find((candidate) => candidate.toLowerCase() === wanted) ?? null;
};
export const watermarkSettingZod = z.object({
    enabled: z.boolean(),
    logoUrl: z.url().nullable(),
    position: watermarkPositionZod,
    scale: z.number().min(0.05).max(0.5),
    opacity: z.number().min(0).max(1),
    marginPx: z.number().int().min(0).max(500),
});
/** Most characters of the admin's "extra instructions added to every look". */
export const TRYON_SETTINGS_INSTRUCTIONS_MAX = 1000;
export const tryonSettingZod = z.object({
    aspect: z.enum(["3:4", "4:5", "1:1", "9:16"]),
    // Extra instructions added to every look (sent to Refract as
    // `instructions`, which it appends to its own prompt). Trimmed; empty =
    // none. Replaces the old `promptOverride` (which replaced Refract's
    // prompt): a stored row that still has one is mapped on read by the
    // settings service. The default keeps rows from before this field parseable.
    instructions: zString
        .max(TRYON_SETTINGS_INSTRUCTIONS_MAX)
        .nullable()
        .default(null)
        .transform((text) => text || null),
});
// The old `share` key (WhatsApp text templates + "append image link") was
// removed: sharing always sends the image files. A stored `share` row is
// ignored on read, and a `share` field in an update is stripped.
export const retailerSettingsZod = z.object({
    branding: brandingSettingZod,
    watermark: watermarkSettingZod,
    tryon: tryonSettingZod,
});
export const retailerSettingsUpdateInputZod = retailerSettingsZod.partial();
//# sourceMappingURL=retailer_settings.zod.js.map